import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { DesktopLayout } from "../../components/DesktopLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { usuarios } from "../../data/mock";
import {
  Users, Clock, QrCode, RefreshCw, CheckCircle2, Key, X, Loader2, PlayCircle,
} from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const POLL_INTERVAL = 30000;

async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const vigilantesParaGerenciar = usuarios.filter(
  (u) => u.tipo === "vigilante" && u.ativo,
);

const fmtHora = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—";

const fmtElapsed = (iso) => {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
};

export default function EfetivoAtual() {
  const [vigilantes,   setVigilantes]   = useState([]);
  const [plantoes,     setPlantoes]     = useState([]);
  const [fotos,        setFotos]        = useState({});
  const [loading,      setLoading]      = useState(true);
  const [atualizando,  setAtualizando]  = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  // PIN Temporário
  const [modalPin, setModalPin] = useState(null);
  const [pinDigitosTemp, setPinDigitosTemp] = useState("");
  const [pinSaving, setPinSaving] = useState(false);
  const [pinOk, setPinOk] = useState(false);
  const [pinErro, setPinErro] = useState("");

  const carregar = useCallback(async (mostrarSpinner = false) => {
    if (mostrarSpinner) setAtualizando(true);

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const [{ data, error }, { data: perfisData }, { data: plantoesData }] = await Promise.all([
      supabase
        .from("liberty_ronda_pontos")
        .select("*")
        .eq("empresa_id", EMPRESA_ID)
        .gte("created_at", hoje.toISOString())
        .order("created_at", { ascending: false }),
      supabase
        .from("liberty_perfil_vigilante")
        .select("vigilante_id, foto_url")
        .eq("empresa_id", EMPRESA_ID),
      supabase
        .from("liberty_plantoes")
        .select("id, vigilante_id, vigilante_nome, inicio")
        .eq("empresa_id", EMPRESA_ID)
        .is("fim", null)
        .order("inicio", { ascending: false }),
    ]);

    if (error) {
      console.error("[EfetivoAtual] Supabase:", error.message);
    } else {
      const map = {};
      for (const r of data || []) {
        const id = r.vigilante_id || r.vigilante_nome || "unknown";
        if (!map[id]) {
          map[id] = { id, nome: r.vigilante_nome || "Vigilante", ultimoScan: r.created_at, pontos: [] };
        }
        map[id].pontos.push({ ponto: r.ponto, validado_em: r.created_at });
        if (new Date(r.created_at) > new Date(map[id].ultimoScan)) {
          map[id].ultimoScan = r.created_at;
        }
      }
      setVigilantes(Object.values(map));

      const fotosMap = {};
      for (const p of perfisData || []) {
        if (p.vigilante_id && p.foto_url) fotosMap[p.vigilante_id] = p.foto_url;
      }
      setFotos(fotosMap);
      setPlantoes(plantoesData || []);
    }

    setUltimaAtualizacao(new Date());
    setLoading(false);
    if (mostrarSpinner) setAtualizando(false);
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(() => carregar(), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [carregar]);

  // ── PIN Temporário ──────────────────────────────────────────────────────
  const abrirModalPin = (usuario) => {
    setModalPin(usuario);
    setPinDigitosTemp("");
    setPinOk(false);
    setPinErro("");
  };

  const fecharModalPin = () => {
    if (pinSaving) return;
    setModalPin(null);
    setPinDigitosTemp("");
    setPinOk(false);
    setPinErro("");
  };

  const adicionarDigitoTemp = (d) => {
    if (pinDigitosTemp.length >= 4 || pinSaving) return;
    setPinDigitosTemp((p) => p + d);
  };

  const removerDigitoTemp = () => {
    if (pinSaving) return;
    setPinDigitosTemp((p) => p.slice(0, -1));
  };

  const salvarPinTemporario = async () => {
    if (pinDigitosTemp.length !== 4 || pinSaving) return;
    setPinSaving(true);
    setPinErro("");
    try {
      const hash = await sha256Hex(pinDigitosTemp);

      // Verifica se o vigilante já tem registro em liberty_pins
      const { data: existing } = await supabase
        .from("liberty_pins")
        .select("id")
        .eq("vigilante_id", modalPin.id)
        .maybeSingle();

      let error;
      if (existing) {
        // Registro existe → apenas atualiza campos de PIN temporário
        ({ error } = await supabase
          .from("liberty_pins")
          .update({
            pin_temporario: hash,
            pin_temporario_plain: pinDigitosTemp,
            forcar_troca: true,
          })
          .eq("vigilante_id", modalPin.id));
      } else {
        // Sem registro → insere com pin_hash preenchido (obrigatório NOT NULL)
        ({ error } = await supabase
          .from("liberty_pins")
          .insert({
            vigilante_id: modalPin.id,
            vigilante_nome: modalPin.nome,
            pin_hash: hash,
            pin_temporario: hash,
            pin_temporario_plain: pinDigitosTemp,
            forcar_troca: true,
            empresa_id: EMPRESA_ID,
          }));
      }

      if (error) {
        console.error("[PIN temp] Supabase error:", error.code, error.message, error.details);
        const msg = error.code === "42501"
          ? `Sem permissão (RLS): ${error.message}`
          : `Erro ao salvar [${error.code}]: ${error.message}`;
        setPinErro(msg);
        setPinSaving(false);
        return;
      }

      setPinOk(true);
      setPinSaving(false);
    } catch (e) {
      console.error("[PIN temp]", e);
      setPinErro("Erro ao salvar. Tente novamente.");
      setPinSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <DesktopLayout
      title="Efetivo Atual"
      subtitle="Vigilantes com atividade registrada hoje via QR Code"
      actions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => carregar(true)}
            disabled={atualizando}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-xs font-medium hover:bg-white/10 hover:text-white transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${atualizando ? "animate-spin" : ""}`} />
            Atualizar
          </button>
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-emerald-500 live-dot" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs font-medium text-emerald-300">Ao vivo</span>
          </div>
        </div>
      }
    >
      {/* ── Seção 0: Plantões ativos agora ── */}
      <div className="mb-8">
        <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 mb-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="tiny-label text-slate-500">Em plantão agora</p>
              <h2 className="text-xl font-bold mt-0.5">Plantões em andamento</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-emerald-500 live-dot" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            <span className="text-lg font-bold text-emerald-300">{loading ? "…" : plantoes.length}</span>
            <span className="text-xs text-emerald-400">ativos</span>
          </div>
        </div>

        {!loading && plantoes.length === 0 && (
          <div className="rounded-xl bg-[#0a1128] border border-white/5 px-5 py-6 text-center">
            <p className="text-sm text-slate-500">Nenhum plantão iniciado no momento</p>
          </div>
        )}

        {plantoes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {plantoes.map((p) => (
              <div key={p.id} className="rounded-xl bg-[#0a1128] border border-emerald-500/20 p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  {fotos[p.vigilante_id] ? (
                    <img src={fotos[p.vigilante_id]} alt={p.vigilante_nome} className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <span className="text-sm font-bold text-emerald-300">
                      {(p.vigilante_nome || "VG").split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm truncate">{p.vigilante_nome || "Vigilante"}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Clock className="w-3 h-3 text-slate-500" />
                    <p className="text-xs text-slate-400">Início: <span className="font-mono text-slate-300">{fmtHora(p.inicio)}</span></p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-slate-500">há</p>
                  <p className="text-sm font-bold text-emerald-300">{fmtElapsed(p.inicio)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Seção 1: Vigilantes ativos hoje ── */}
      <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="tiny-label text-slate-500">Plantão atual</p>
          <h2 className="text-xl font-bold mt-1">Vigilantes em serviço hoje</h2>
          <p className="text-sm text-slate-400 mt-1">
            Baseado nos registros de QR Code do dia. Atualiza automaticamente a cada 30 segundos.
          </p>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <Users className="w-4 h-4 text-emerald-300" />
            <span className="text-lg font-bold text-emerald-300">
              {loading ? "…" : vigilantes.length}
            </span>
            <span className="text-xs text-emerald-400">ativos</span>
          </div>
          {ultimaAtualizacao && (
            <p className="text-[11px] text-slate-500 font-mono-data">
              Atualizado às{" "}
              {ultimaAtualizacao.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </p>
          )}
        </div>
      </div>

      {loading && (
        <p className="text-sm text-slate-500 italic text-center py-12">Carregando efetivo…</p>
      )}

      {!loading && vigilantes.length === 0 && (
        <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-10 text-center">
          <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="font-semibold text-slate-400">Nenhum registro hoje</p>
          <p className="text-sm text-slate-500 mt-1">
            Aguardando confirmação de pontos de ronda pelos vigilantes.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {vigilantes.map((v) => (
          <div
            key={v.id}
            className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 hover:border-white/20 transition"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/10 shrink-0">
                  {fotos[v.id] ? (
                    <img src={fotos[v.id]} alt={v.nome} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#0d1b3e] flex items-center justify-center font-display font-bold text-sm text-white">
                      {v.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-bold text-white">{v.nome}</p>
                  <p className="text-xs text-slate-500">Em serviço hoje</p>
                </div>
              </div>
              <StatusBadge tone="success">ativo</StatusBadge>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <QrCode className="w-4 h-4 text-emerald-300" />
                <span className="text-sm text-emerald-300 font-semibold">
                  {v.pontos.length} ponto(s) confirmado(s)
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10">
                <Clock className="w-4 h-4 text-blue-300" />
                <span className="text-sm text-slate-300">
                  Último:{" "}
                  <span className="font-mono-data text-blue-300">
                    {new Date(v.ultimoScan).toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </span>
              </div>
            </div>

            <div className="border-t border-white/5 pt-3">
              <p className="text-[11px] text-slate-500 mb-2">Pontos confirmados</p>
              <div className="flex flex-wrap gap-1.5">
                {v.pontos.slice(0, 6).map((p, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-slate-300"
                  >
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                    {p.ponto || "Ponto"}
                  </span>
                ))}
                {v.pontos.length > 6 && (
                  <span className="px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-slate-500">
                    +{v.pontos.length - 6}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Seção 2: Gerenciamento de PIN ── */}
      <div className="mt-10">
        <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Key className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <p className="tiny-label text-slate-500">Acesso</p>
              <h2 className="text-xl font-bold mt-0.5">PIN Temporário</h2>
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-3">
            Defina um PIN temporário para um vigilante. No próximo acesso, ele será obrigado a criar um novo PIN permanente.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {vigilantesParaGerenciar.map((u) => (
            <div
              key={u.id}
              className="rounded-xl bg-[#0a1128] border border-white/5 p-4 flex items-center justify-between gap-3 hover:border-white/10 transition"
            >
              <div className="min-w-0">
                <p className="font-semibold text-sm text-white truncate">{u.nome}</p>
                <p className="text-xs text-slate-500">Vigilante</p>
              </div>
              <button
                onClick={() => abrirModalPin(u)}
                className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-semibold hover:bg-amber-500/20 active:scale-95 transition"
              >
                <Key className="w-3.5 h-3.5" />
                Definir PIN
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modal PIN Temporário ── */}
      {modalPin && (
        <div
          className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={fecharModalPin}
        >
          <div
            className="w-full max-w-xs rounded-2xl bg-[#0f1529] border border-white/10 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {pinOk ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <p className="font-bold text-lg text-white mb-1">PIN temporário definido!</p>
                <p className="text-sm text-slate-400 mb-5">
                  {modalPin.nome} deverá criar um novo PIN no próximo acesso.
                </p>
                <button
                  onClick={fecharModalPin}
                  className="w-full py-3 rounded-xl bg-emerald-500 text-black font-bold text-sm hover:bg-emerald-400 transition"
                >
                  Fechar
                </button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <p className="font-bold text-white">Definir PIN Temporário</p>
                    <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[180px]">
                      {modalPin.nome}
                    </p>
                  </div>
                  <button
                    onClick={fecharModalPin}
                    className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                  Comunique este PIN ao vigilante. Ele será obrigado a criar um PIN permanente no próximo login.
                </p>

                {/* Dots */}
                <div className="flex items-center justify-center gap-4 mb-5">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
                        i < pinDigitosTemp.length
                          ? "bg-amber-400 border-amber-400 scale-110"
                          : "border-white/20"
                      }`}
                    />
                  ))}
                </div>

                {/* Numpad */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => adicionarDigitoTemp(String(n))}
                      disabled={pinSaving}
                      className="h-12 rounded-xl bg-white/5 border border-white/10 text-xl font-bold text-white hover:bg-white/10 active:scale-95 transition disabled:opacity-30 select-none"
                    >
                      {n}
                    </button>
                  ))}
                  <div />
                  <button
                    type="button"
                    onClick={() => adicionarDigitoTemp("0")}
                    disabled={pinSaving}
                    className="h-12 rounded-xl bg-white/5 border border-white/10 text-xl font-bold text-white hover:bg-white/10 active:scale-95 transition disabled:opacity-30 select-none"
                  >
                    0
                  </button>
                  <button
                    type="button"
                    onClick={removerDigitoTemp}
                    disabled={pinSaving || pinDigitosTemp.length === 0}
                    className="h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 active:scale-95 transition disabled:opacity-30 select-none"
                  >
                    <span className="text-lg leading-none">⌫</span>
                  </button>
                </div>

                {pinErro && (
                  <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                    {pinErro}
                  </div>
                )}

                <button
                  type="button"
                  onClick={salvarPinTemporario}
                  disabled={pinDigitosTemp.length !== 4 || pinSaving}
                  className="w-full py-3 rounded-xl bg-amber-500 text-black font-bold text-sm disabled:opacity-40 hover:bg-amber-400 active:scale-[0.98] transition flex items-center justify-center gap-2"
                >
                  {pinSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  {pinSaving ? "Salvando…" : "Definir PIN Temporário"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </DesktopLayout>
  );
}
